// components/secure/HrLawLibraryPanel.js — HR Compliance → Law library.
//
// Every employment-law rule the checklists draw on, with its citation, thresholds and
// dates. The managing broker can add, edit, retire, reinstate and stamp a rule as
// verified; the office manager can read. Nothing is deleted: retiring sets the last
// day in force, and each change is kept in the rule's history. A rule edited here is
// left alone by the seed script (scripts/seed-hr-requirements.js).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../shared/DataList';

const JUR = { federal: 'Federal', illinois: 'Illinois', cook_county: 'Cook County', chicago: 'Chicago', florida: 'Florida', firm: 'Firm' };
const JUR_STYLE = { federal: 'bg-blue-100 text-blue-800', illinois: 'bg-emerald-100 text-emerald-800',
    cook_county: 'bg-amber-100 text-amber-800', chicago: 'bg-purple-100 text-purple-800', florida: 'bg-orange-100 text-orange-800', firm: 'bg-slate-200 text-slate-700' };
const STATUS = { in_force: ['In force', 'bg-green-100 text-green-800'], retiring: ['Retiring', 'bg-amber-100 text-amber-800'],
    retired: ['Retired', 'bg-slate-200 text-slate-600'], future: ['Not yet in force', 'bg-sky-100 text-sky-800'], inactive: ['Inactive', 'bg-slate-200 text-slate-600'] };
const CHANGE = { created: 'Added', edited: 'Edited', retired: 'Retired', reinstated: 'Reinstated', verified: 'Verified',
    seed_update: 'Updated by the seed', seed_insert: 'Loaded by the seed' };
const LABEL = {
    title: 'Title', applies_to: 'Applies to', stage: 'Stage', jurisdiction: 'Jurisdiction', authority: 'Citation',
    action: 'What to do', min_employees: 'Min. employees', max_employees: 'Max. employees', location_scope: 'Where',
    condition_key: 'Condition', case_types: 'Case types', deadline_days: 'Deadline (days)', deadline_unit: 'Day type',
    deadline_from: 'Counted from', deadline_note: 'Deadline note', recurrence: 'Recurrence', form_key: 'Form',
    is_blocking: 'Blocking', sort_order: 'Sort order', is_active: 'Active', effective_from: 'In force from', effective_to: 'Last day in force',
};
const input = 'border border-slate-300 rounded px-2 py-1 text-sm w-full';
const btn = 'px-3 py-1.5 text-sm bg-slate-800 text-white rounded disabled:bg-indigo-300';
const btn2 = 'px-3 py-1.5 text-sm border border-slate-300 rounded text-slate-700';
const nice = (s) => String(s ?? '').replace(/_/g, ' ');
const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString() : '—');
const today = () => new Date().toISOString().slice(0, 10);
const show = (v) => (v == null || v === '' ? '—' : String(v));

const Badge = ({ j }) => <span className={`px-2 py-0.5 rounded text-xs font-medium ${JUR_STYLE[j] || JUR_STYLE.firm}`}>{JUR[j] || j}</span>;
// Headcount bounds, deadline and end date of a requirement, as one line.
const sizeText = (r) => [
    r.min_employees != null ? `${r.min_employees}+ employees` : '', r.max_employees != null ? `up to ${r.max_employees}` : '',
    r.deadline_days != null ? `${r.deadline_days} ${r.deadline_unit} days` : '', r.deadline_from === 'year_end' ? 'by 31 Dec' : '',
    r.effective_to ? `last day ${fmtDate(r.effective_to)}` : ''].filter(Boolean).join(' · ');
const StatusBadge = ({ s }) => <span className={`px-2 py-0.5 rounded text-xs ${STATUS[s]?.[1] || ''}`}>{STATUS[s]?.[0] || s}</span>;

const L = ({ label, children, wide }) => <label className={`block text-sm ${wide ? 'md:col-span-2' : ''}`}><span className="text-slate-600">{label}</span>{children}</label>;

const Modal = ({ title, onClose, children }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start p-4 overflow-y-auto">
        <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl my-8">
            <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-slate-800">{title}</h2>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none" aria-label="Close">&times;</button>
            </div>
            {children}
        </div>
    </div>
);

const EMPTY = { req_key: '', title: '', applies_to: 'employer', stage: 'posting', jurisdiction: 'federal', authority: '', action: '',
    min_employees: '', max_employees: '', location_scope: 'any', condition_key: '', case_types: '', deadline_days: '',
    deadline_unit: 'calendar', deadline_from: 'none', deadline_note: '', recurrence: 'once', form_key: '', is_blocking: false,
    sort_order: 0, is_active: true, effective_from: '', effective_to: '', change_note: '', verified: false };

// ---------------------------------------------------------------- the edit form
const EditForm = ({ start, choices, creating, onSaved, onCancel }) => {
    const api = useApiClient();
    const [f, setF] = useState(() => {
        const o = { ...EMPTY };
        for (const k of Object.keys(EMPTY)) if (start && start[k] != null) o[k] = start[k];
        o.is_blocking = !!Number(o.is_blocking); o.is_active = !!Number(o.is_active ?? 1);
        o.change_note = ''; o.verified = false;
        return o;
    });
    const [problems, setProblems] = useState([]);
    const [busy, setBusy] = useState(false);
    const V = (k) => ({ value: f[k] ?? '', onChange: (e) => setF((p) => ({ ...p, [k]: e.target.value })) });
    const C = (k) => ({ checked: !!f[k], onChange: (e) => setF((p) => ({ ...p, [k]: e.target.checked })) });
    // Plain functions, not components: a component defined in render remounts on every
    // keystroke and the field loses focus.
    const Sel = ({ k, opts, blank }) => (
        <select className={input} {...V(k)}>
            {blank && <option value="">{blank}</option>}
            {opts.map((o) => <option key={o} value={o}>{nice(o)}</option>)}
        </select>
    );
    const caseList = String(f.case_types || '').split(',').filter(Boolean);
    const toggleCase = (c) => setF((p) => {
        const l = String(p.case_types || '').split(',').filter(Boolean);
        return { ...p, case_types: (l.includes(c) ? l.filter((x) => x !== c) : [...l, c]).join(',') };
    });

    const save = async () => {
        setBusy(true); setProblems([]);
        try {
            const r = creating ? await api.post('/api/hr/library', f) : await api.put(`/api/hr/library/${start.req_key}`, f);
            onSaved(r);
        } catch (e) { setProblems(e.problems || [e.message]); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
                {creating && <L label="Key (permanent, e.g. hire_il_w4)"><input className={input} {...V('req_key')} /></L>}
                <L label="Title" wide={!creating}><input className={input} {...V('title')} /></L>
                <L label="Applies to">{Sel({ k: "applies_to", opts: choices.applies_to })}</L>
                <L label="Stage">{Sel({ k: "stage", opts: choices.stage })}</L>
                <L label="Jurisdiction">{Sel({ k: "jurisdiction", opts: choices.jurisdiction })}</L>
                <L label="Where the employee works">{Sel({ k: "location_scope", opts: choices.location_scope })}</L>
                <L label="Citation (statute, rule, ordinance)" wide><input className={input} {...V('authority')} /></L>
                <L label="What to do" wide><textarea rows={3} className={input} {...V('action')} /></L>
                <L label="Applies from this many employees (blank = any)"><input type="number" min="0" className={input} {...V('min_employees')} /></L>
                <L label="Up to this many employees (blank = no cap)"><input type="number" min="0" className={input} {...V('max_employees')} /></L>
                <L label="Only when (condition)">{Sel({ k: "condition_key", opts: choices.conditions, blank: "— always —" })}</L>
                <div className="text-sm"><span className="text-slate-600">Case types (employee rules)</span>
                    <div className="flex flex-wrap gap-3 mt-1">{choices.case_types.map((c) => (
                        <label key={c} className="flex items-center gap-1"><input type="checkbox" checked={caseList.includes(c)} onChange={() => toggleCase(c)} />{nice(c)}</label>))}
                    </div>
                </div>
                <L label="Deadline (days, blank = none)"><input type="number" min="0" className={input} {...V('deadline_days')} /></L>
                <L label="Day type">{Sel({ k: "deadline_unit", opts: choices.deadline_unit })}</L>
                <L label="Counted from">{Sel({ k: "deadline_from", opts: choices.deadline_from })}</L>
                <L label="Recurrence">{Sel({ k: "recurrence", opts: choices.recurrence })}</L>
                <L label="Deadline note" wide><input className={input} {...V('deadline_note')} /></L>
                <L label="Form key (blank = none)"><input className={input} {...V('form_key')} /></L>
                <L label="Sort order"><input type="number" className={input} {...V('sort_order')} /></L>
                <L label="In force from (blank = always)"><input type="date" className={input} {...V('effective_from')} /></L>
                <L label="Last day in force (blank = open; use Retire to end a rule)"><input type="date" className={input} {...V('effective_to')} /></L>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" {...C('is_blocking')} />Blocking — the person cannot start / the case cannot close without it</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" {...C('is_active')} />Active</label>
            </div>
            <L label={creating ? 'Note for the history (optional)' : 'Why it changed (required: the source, or what was wrong)'} wide>
                <input className={input} {...V('change_note')} />
            </L>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" {...C('verified')} />I checked this against the source today (stamps verified on / by)</label>
            {problems.length > 0 && <ul className="text-sm text-red-600 list-disc pl-5">{problems.map((p) => <li key={p}>{p}</li>)}</ul>}
            <div className="flex gap-2">
                <button className={btn} disabled={busy} onClick={save}>{creating ? 'Add requirement' : 'Save changes'}</button>
                <button className={btn2} onClick={onCancel}>Cancel</button>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------- one rule
const Detail = ({ reqKey, choices, canEdit, onClose, onChanged }) => {
    const api = useApiClient();
    const [d, setD] = useState(null);
    const [mode, setMode] = useState('view');
    const [retire, setRetire] = useState({ effective_to: today(), reason: '' });
    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');

    const load = useCallback(() => api.get(`/api/hr/library/${reqKey}`).then(setD).catch((e) => setError(e.message)), [api, reqKey]);
    useEffect(() => { load(); }, [load]);

    const act = async (fn) => {
        setError(''); setMsg('');
        try { const r = await fn(); setMsg(r?.message || 'Saved.'); setMode('view'); await load(); onChanged(); }
        catch (e) { setError(e.message); }
    };

    if (!d) return <Modal title="Requirement" onClose={onClose}>{error ? <p className="text-red-600">{error}</p> : <p>Loading…</p>}</Modal>;
    const r = d.requirement;
    return (
        <Modal title={r.title} onClose={onClose}>
            <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
                <Badge j={r.jurisdiction} /><StatusBadge s={r.status} />
                <span className="font-mono text-xs text-slate-500">{r.req_key}</span>
                <span className="text-xs text-slate-500">{r.origin === 'owner' ? 'Added on this screen' : 'From the seed library'}{Number(r.owner_edited) ? ' · edited by the managing broker (the seed will not overwrite it)' : ''}</span>
            </div>
            <p className="text-sm mb-3">
                {r.verified_on ? <>Verified on <strong>{fmtDate(r.verified_on)}</strong> by <strong>{r.verified_by_name || '—'}</strong></> : <span className="text-amber-700">Not verified against the source.</span>}
            </p>
            {msg && <p className="text-sm text-green-700 mb-2">{msg}</p>}
            {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

            {mode === 'edit' && <EditForm start={r} choices={choices} onCancel={() => setMode('view')}
                onSaved={(x) => { setMsg(x?.message || 'Saved.'); setMode('view'); load(); onChanged(); }} />}

            {mode === 'retire' && (
                <div className="space-y-2 text-sm border rounded p-3 mb-3">
                    <p>Retiring keeps the rule and its history; it stops appearing in cases opened after the last day in force.</p>
                    <label className="block">Last day in force<input type="date" className={input} value={retire.effective_to} onChange={(e) => setRetire((p) => ({ ...p, effective_to: e.target.value }))} /></label>
                    <label className="block">Reason (repealed, superseded by …, entered in error)<input className={input} value={retire.reason} onChange={(e) => setRetire((p) => ({ ...p, reason: e.target.value }))} /></label>
                    <div className="flex gap-2">
                        <button className={btn} onClick={() => act(() => api.post(`/api/hr/library/${r.req_key}/retire`, retire))}>Retire</button>
                        <button className={btn2} onClick={() => setMode('view')}>Cancel</button>
                    </div>
                </div>
            )}

            {mode === 'view' && (
                <>
                    {canEdit && (
                        <div className="flex flex-wrap gap-2 mb-3">
                            <button className={btn} onClick={() => setMode('edit')}>Edit</button>
                            <button className={btn2} onClick={() => act(() => api.post(`/api/hr/library/${r.req_key}/verify`, {}))}>Mark verified today</button>
                            {r.effective_to
                                ? <button className={btn2} onClick={() => { const reason = window.prompt('Why is this rule back in force?'); if (reason) act(() => api.post(`/api/hr/library/${r.req_key}/reinstate`, { reason })); }}>Reinstate</button>
                                : <button className={btn2} onClick={() => setMode('retire')}>Retire…</button>}
                        </div>
                    )}
                    <dl className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm mb-4">
                        {Object.keys(LABEL).map((k) => (
                            <div key={k} className={['action', 'authority'].includes(k) ? 'md:col-span-2' : ''}>
                                <dt className="text-xs text-slate-500">{LABEL[k]}</dt>
                                <dd>{['is_blocking', 'is_active'].includes(k) ? (Number(r[k]) ? 'Yes' : 'No')
                                    : ['effective_from', 'effective_to'].includes(k) ? (r[k] ? fmtDate(r[k]) : '—')
                                    : ['applies_to', 'stage', 'location_scope', 'condition_key', 'deadline_unit', 'deadline_from', 'recurrence', 'case_types'].includes(k) ? (r[k] ? nice(r[k]) : '—')
                                    : show(r[k])}</dd>
                            </div>
                        ))}
                        {r.retired_reason && <div className="md:col-span-2"><dt className="text-xs text-slate-500">Retired because</dt><dd>{r.retired_reason}</dd></div>}
                    </dl>
                </>
            )}

            <h3 className="font-semibold text-slate-700 mb-1">Change history</h3>
            {!d.history.length && <p className="text-sm text-slate-500">No recorded changes yet (history starts when the editor was installed).</p>}
            <ul className="divide-y text-sm">
                {d.history.map((h) => (
                    <li key={h.history_id} className="py-2">
                        <div><strong>{CHANGE[h.change_type] || h.change_type}</strong> — {h.changed_at ? new Date(h.changed_at).toLocaleString() : ''} — {h.changed_by_name || '—'}</div>
                        {h.note && <div className="text-slate-600">{h.note}</div>}
                        {h.changed_fields && typeof h.changed_fields === 'object' && (
                            <ul className="text-xs text-slate-500 pl-4 list-disc">
                                {Object.entries(h.changed_fields).map(([k, v]) => (
                                    <li key={k}>{LABEL[k] || k}: <span className="line-through">{show(v?.from)}</span> → {show(v?.to)}</li>
                                ))}
                            </ul>
                        )}
                    </li>
                ))}
            </ul>
        </Modal>
    );
};

// ---------------------------------------------------------------- the list
export default function HrLawLibraryPanel({ onChanged = () => {} }) {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState('current');
    const [jur, setJur] = useState('');
    const [open, setOpen] = useState(null);
    const [adding, setAdding] = useState(false);
    const [msg, setMsg] = useState('');

    const load = useCallback(() => api.get('/api/hr/library').then(setData).catch((e) => setError(e.message)), [api]);
    useEffect(() => { load(); }, [load]);
    const changed = () => { load(); onChanged(); };

    const rows = useMemo(() => {
        if (!data) return [];
        return data.requirements.filter((r) => {
            if (statusFilter === 'current' && !['in_force', 'retiring', 'future'].includes(r.status)) return false;
            if (statusFilter === 'retired' && !['retired', 'inactive'].includes(r.status)) return false;
            if (statusFilter === 'unverified' && r.verified_on) return false;
            return !jur || r.jurisdiction === jur;
        });
    }, [data, statusFilter, jur]);

    if (error) return <div className="bg-white rounded-lg shadow p-4 text-red-600">{error}</div>;
    if (!data) return <div className="bg-white rounded-lg shadow p-4">Loading the law library…</div>;

    return (
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
            <p className="text-sm text-slate-600 mb-3">
                Every rule the checklists draw on, with its citation. This is a compliance aid, not legal advice; have employment counsel review the templates and any separation, non-compete or adverse decision.
                {data.can_edit ? ' Changes apply to cases opened afterwards; open cases keep the wording they started with.' : ' Only the managing broker can change the library.'}
            </p>
            <div className="flex flex-wrap gap-2 items-end mb-3">
                <select className="border border-slate-300 rounded px-2 py-1 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="current">In force (and upcoming)</option><option value="retired">Retired</option>
                    <option value="unverified">Not verified</option><option value="all">All</option>
                </select>
                <select className="border border-slate-300 rounded px-2 py-1 text-sm" value={jur} onChange={(e) => setJur(e.target.value)}>
                    <option value="">Every jurisdiction</option>
                    {data.choices.jurisdiction.map((j) => <option key={j} value={j}>{JUR[j] || j}</option>)}
                </select>
                <span className="text-xs text-slate-500">{rows.length} of {data.requirements.length}</span>
                {data.can_edit && <button className={`${btn} ml-auto`} onClick={() => setAdding(true)}>Add requirement</button>}
            </div>
            {msg && <p className="text-sm text-green-700 mb-2">{msg}</p>}
            <DataList rows={rows} rowKey="req_key" csvName="hr-law-library" onRowClick={(r) => setOpen(r.req_key)} empty="Nothing matches."
                defaultSort={{ key: 'title', dir: 'asc' }}
                filters={[{ key: 'applies', label: 'Applies to' }, { key: 'verified', label: 'Verified' }]}
                columns={[
                    { key: 'jurisdiction', label: 'Jurisdiction', value: (r) => JUR[r.jurisdiction] || r.jurisdiction, render: (r) => <Badge j={r.jurisdiction} /> },
                    { key: 'applies', label: 'Applies to', value: (r) => (r.applies_to === 'employer' ? 'firm' : nice(r.case_types)),
                        render: (r) => <span className="text-xs uppercase text-slate-500">{r.applies_to === 'employer' ? 'firm' : r.case_types}</span> },
                    { key: 'title', label: 'Requirement', value: (r) => `${r.title} ${r.req_key} ${r.action || ''}`,
                        render: (r) => <span className="flex flex-wrap items-center gap-2"><strong>{r.title}</strong>
                            {r.status !== 'in_force' && <StatusBadge s={r.status} />}
                            {Number(r.owner_edited) ? <span className="text-xs text-indigo-700">edited</span> : null}</span> },
                    { key: 'status', label: 'Status', value: (r) => STATUS[r.status]?.[0] || r.status },
                    { key: 'authority', label: 'Citation', render: (r) => <span className="text-xs text-slate-600">{r.authority}</span> },
                    { key: 'size', label: 'Size / deadline', sortable: false, value: sizeText, render: (r) => <span className="text-xs text-slate-500">{sizeText(r)}</span> },
                    { key: 'verified', label: 'Verified', value: (r) => (r.verified_on ? 'verified' : 'not verified'),
                        render: (r) => <span className="text-xs text-slate-500">{r.verified_on ? `verified ${fmtDate(r.verified_on)} by ${r.verified_by_name || '—'}` : 'not verified'}</span> },
                    { key: 'verified_on', label: 'Verified on', type: 'date' },
                ]} />

            {open && <Detail reqKey={open} choices={data.choices} canEdit={data.can_edit} onClose={() => setOpen(null)} onChanged={changed} />}
            {adding && (
                <Modal title="Add a requirement" onClose={() => setAdding(false)}>
                    <EditForm creating choices={data.choices} onCancel={() => setAdding(false)}
                        onSaved={(r) => { setAdding(false); setMsg(r?.message || 'Added.'); changed(); }} />
                </Modal>
            )}
        </div>
    );
}
